import React, { useState } from "react";
import { IconButton } from "@material-ui/core";
import LightTooltip from "./LightTooltip";
import { EditOutlined } from "@material-ui/icons";
import { Pane, Text, CDialog, HFlex } from "shared";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { SPACING, Breakpoint } from "theme";
import { css } from "@emotion/css";
import { useHealthCareOrganization } from "../queries/useHealthCareOrganizations";
import { HcoPricingForm } from "./HcoPricingForm";

const HcoPricingSection: React.VFC = () => {
  const { t } = useTranslation();
  const { item } = useParams<{ item: string }>();
  const { data: hco } = useHealthCareOrganization(Number(item) || 0);
  const [isDialogOpen, setDialogOpen] = useState(false);

  if (!hco) return null;

  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  const platformFeeDisplay = (() => {
    if (!hco.platform_fee_model) return null;
    if (hco.platform_fee_model === "percentage") {
      return hco.platform_fee_bps != null ? `${hco.platform_fee_bps / 100}%` : hco.platform_fee_model;
    }
    return hco.platform_fee_cents != null ? `${hco.platform_fee_cents} cents` : hco.platform_fee_model;
  })();

  const details = [
    [t("form.hco_platform_fee_model"), hco.platform_fee_model],
    [t("form.hco_platform_fee"), platformFeeDisplay],
    [t("form.hco_seats"), hco.seats != null && hco.seats > 0 ? String(hco.seats) : null],
  ].filter(([, value]) => value != null && value !== "");

  return (
    <>
      <Text paragraph caption secondary>
        {t("form.hco_pricing")}
      </Text>

      <Pane>
        <Pane dark mb={2}>
          <HFlex justifyContent="space-between" alignItems="flex-start">
            <div className={styles.grid}>
              {details.map(([label, value]) => (
                <React.Fragment key={String(label)}>
                  <Text light nowrap>
                    {label}
                  </Text>
                  <Text bold nowrap>
                    {value}
                  </Text>
                </React.Fragment>
              ))}
            </div>
            <LightTooltip title={t("form.hco_edit_pricing")}>
              <IconButton onClick={openDialog} size="small" aria-label={t("form.hco_edit_pricing")}>
                <EditOutlined fontSize="small" style={{ color: "#1976d2" }} />
              </IconButton>
            </LightTooltip>
          </HFlex>
        </Pane>
      </Pane>

      <CDialog maxWidth="xs" fullWidth open={isDialogOpen} onClose={closeDialog}>
        <HcoPricingForm hco={hco} onSuccess={closeDialog} onCancel={closeDialog} />
      </CDialog>
    </>
  );
};

export default HcoPricingSection;

const styles = {
  grid: css`
    overflow: auto;
    display: grid;
    grid-auto-rows: 45px;
    align-items: center;
    grid-column-gap: ${SPACING}px;
    @media screen and (max-width: ${Breakpoint.sm - 1}px) {
      grid-template-columns: 1fr;
    }
    @media screen and (min-width: ${Breakpoint.sm}px) {
      grid-template-columns: auto 1fr;
    }
  `,
};
